import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Leaf, Brain, Laugh, Clock, ArrowLeft, Check, Loader2, Award } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRACTITIONERS = [
  {
    id: "stress-coach",
    title: "Stress Management & Life Skills Counseling Coach",
    description: "Professional guidance for managing stress, building resilience, and developing essential life skills through trauma-informed, culturally sensitive approaches.",
    specialties: ["Stress Management", "Life Skills Development", "Resilience Building", "Coping Strategies", "Goal Setting"],
    duration: "50 min",
    price: "Free consultation / Premium session",
    color: "bg-spa-sage/10 text-spa-sage border-spa-sage/20"
  },
  {
    id: "drama-therapist",
    title: "Laughing Drama Therapist OnCALL+ Live",
    description: "Mr. RAPsoul.R's signature laughter therapy and creative drama sessions for emotional release, healing, and joy — available live and on-demand.",
    specialties: ["Laughter Therapy", "Creative Drama", "Emotional Release", "Group Sessions", "Holistic Healing"],
    duration: "45 min",
    price: "Free intro / Premium live session",
    color: "bg-spa-terracotta/10 text-spa-terracotta border-spa-terracotta/20"
  }
];

export default function BookAppointment() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", date: "", time: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/appointments/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          practitioner_type: selected.id,
          name: form.name,
          email: form.email,
          date: form.date,
          time: form.time,
          notes: form.notes
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Booking failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-spa-bone">
      <header className="border-b border-spa-border bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-spa-terracotta rounded-full flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="font-serif text-spa-moss leading-tight">
              <span className="text-sm font-medium">SMAHOO ME.WELL</span><br/>
              <span className="text-sm font-medium">Mind-SPA</span>
              <span className="block text-[9px] font-sans font-normal text-spa-terracotta tracking-wide">Did you SMAHOO Today?</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <a href="/" className="text-spa-text-secondary hover:text-spa-moss text-sm transition-colors flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Home</a>
            <a href="/#auth" className="spa-button-primary text-sm px-4 py-2" data-testid="book-signin-btn">Sign In</a>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12" data-testid="book-appointment-page">
        <div className="text-center mb-10">
          <p className="overline-text mb-2">OnCALL+ Live</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-3">Book Your Appointment</h1>
          <p className="text-spa-text-secondary max-w-xl mx-auto">Choose your wellness practitioner and schedule a session</p>
        </div>

        {submitted ? (
          <div className="spa-card max-w-md mx-auto text-center p-10" data-testid="booking-success">
            <div className="w-16 h-16 bg-spa-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-spa-sage" />
            </div>
            <h2 className="font-serif text-2xl text-spa-moss mb-2">Booking Request Sent!</h2>
            <p className="text-spa-text-secondary mb-6">We'll confirm your appointment with <span className="font-medium text-spa-moss">{selected?.title}</span> shortly via email.</p>
            
            {/* Registration Conversion Prompt */}
            <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200/40 rounded-2xl p-6 mb-6 text-left" data-testid="post-booking-register-prompt">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-spa-moss text-sm">Unlock Your Free Membership</p>
                  <p className="text-xs text-spa-text-secondary">No credit card required</p>
                </div>
              </div>
              <ul className="space-y-2 mb-5">
                <li className="flex items-center gap-2 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Track your booking & appointments from your dashboard
                </li>
                <li className="flex items-center gap-2 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Earn <span className="font-semibold text-amber-600">100 SMAHOO Coins</span> welcome bonus
                </li>
                <li className="flex items-center gap-2 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Access the AI Wellness Coach, Mood Tracker & Community
                </li>
                <li className="flex items-center gap-2 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Earn daily coins and redeem in the Super Store
                </li>
              </ul>
              <a href="/#auth" className="spa-button-primary w-full text-center block py-3" data-testid="post-booking-register-btn">
                Create Your Free Account
              </a>
              <p className="text-center text-xs text-spa-text-secondary mt-3">
                Already a member? <a href="/#auth" className="text-spa-terracotta hover:underline">Sign in</a>
              </p>
            </div>

            <div className="text-center">
              <p className="text-spa-text-secondary text-xs mb-3">Or upgrade to Premium for priority booking & 1-on-1 sessions</p>
              <div className="flex gap-3 justify-center">
                <a href="/pricing" className="px-5 py-2 bg-spa-terracotta/10 text-spa-terracotta rounded-full text-sm font-medium hover:bg-spa-terracotta/20 transition-colors" data-testid="post-booking-pricing-link">View Premium Plans</a>
                <Button variant="outline" className="rounded-full border-spa-border text-sm" onClick={() => { setSubmitted(false); setSelected(null); }}>Book Another</Button>
              </div>
            </div>
          </div>
        ) : !selected ? (
          <div className="grid md:grid-cols-2 gap-6">
            {PRACTITIONERS.map(p => (
              <div key={p.id} className={`spa-card border-2 hover:shadow-lg transition-all cursor-pointer ${p.color}`} onClick={() => setSelected(p)} data-testid={`practitioner-${p.id}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-white/80">
                  {p.id === "stress-coach" ? <Brain className="w-7 h-7 text-spa-sage" /> : <Laugh className="w-7 h-7 text-spa-terracotta" />}
                </div>
                <h3 className="font-serif text-xl font-medium text-spa-moss mb-3">{p.title}</h3>
                <p className="text-spa-text-secondary text-sm mb-4">{p.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.specialties.map(s => <span key={s} className="px-2 py-0.5 bg-white/60 rounded-full text-xs text-spa-moss">{s}</span>)}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-current/10">
                  <span className="flex items-center gap-1 text-sm text-spa-text-secondary"><Clock className="w-4 h-4" /> {p.duration}</span>
                  <span className="text-sm font-medium text-spa-moss">{p.price}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-lg mx-auto">
            <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-spa-text-secondary hover:text-spa-moss mb-6 transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to practitioners
            </button>
            <div className="spa-card mb-6 bg-spa-sage/5 border border-spa-sage/20">
              <h3 className="font-serif text-lg text-spa-moss mb-1">{selected.title}</h3>
              <p className="text-spa-text-secondary text-sm">{selected.duration} session</p>
            </div>
            <form onSubmit={handleSubmit} className="spa-card space-y-4" data-testid="booking-form">
              <h3 className="font-serif text-xl text-spa-moss mb-2">Schedule Your Session</h3>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your full name" className="spa-input" required data-testid="book-name" />
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email address" className="spa-input" required data-testid="book-email" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="spa-input" required data-testid="book-date" />
                <select value={form.time} onChange={(e) => setForm({...form, time: e.target.value})} className="spa-input px-3 py-2 rounded-xl border border-spa-border" required data-testid="book-time">
                  <option value="">Select time</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="13:00">1:00 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                </select>
              </div>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Any notes or specific concerns (optional)" className="w-full spa-input p-3 min-h-[80px] resize-none rounded-xl border border-spa-border" data-testid="book-notes" />
              {error && <p className="text-red-500 text-sm" data-testid="book-error">{error}</p>}
              <Button type="submit" className="spa-button-primary w-full" disabled={submitting} data-testid="book-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : "Request Appointment"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
